import React, { useState, useCallback, useRef } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { UploadTab } from "./tabs/UploadTab";
import { MapTab } from "./tabs/MapTab";
import { DownloadTab } from "./tabs/DownloadTab";
import { ImportFeedbackTab } from "./tabs/ImportFeedbackTab";
import { HistoryTab } from "./tabs/HistoryTab";
import { IMPORT_TYPES } from "@/other/constants/OtherKeys";

const ARCHIVED_EXPORT_TYPES = [
  IMPORT_TYPES.tasks,
  IMPORT_TYPES.resources,
  IMPORT_TYPES.clients,
  IMPORT_TYPES.taskBudget
];

interface ImportEditorProps {
  isShow: boolean;
  context: any;
  record: {
    canEdit: boolean;
    importType: string;
    labelIndex?: number;
    asyncTaskImportResult?: any;
    importHeader: string;
  };
  onClose: () => void;
  editorInstance: any;
}

export const ImportEditor: React.FC<ImportEditorProps> = ({
  context,
  record,
  onClose,
  editorInstance
}) => {
  const [activeTab, setActiveTab] = useState<string>("upload");
  const [mapTabVisible, setMapTabVisible] = useState<boolean>(false);
  const [importFeedbackTabVisible, setImportFeedbackTabVisible] = useState<boolean>(false);
  const [importResponseData, setImportResponseData] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const uploadTabRef = useRef<any>(null);

  const { canEdit, importType, labelIndex = 1, asyncTaskImportResult, importHeader } = record;

  const selectUploadedView = useCallback(
    (importType: string) => {
      const defaultViewOfImportedData = context.viewManager.getDefaultViewByType(importType);
      if (defaultViewOfImportedData) {
        context.viewManager.selectView(defaultViewOfImportedData);
      }
    },
    [context]
  );

  const onAfterRecordsImport = useCallback(
    (importResponseData: any, importType: string) => {
      const { created_count = 0, updated_count = 0 } = importResponseData;
      const totalImportedRecords = created_count + updated_count;

      // Always show the import feedback tab
      setImportFeedbackTabVisible(true);
      setImportResponseData(importResponseData);

      // Only update the view if there were successful imports
      if (totalImportedRecords > 0) {
        selectUploadedView(importType);
      }
    },
    [selectUploadedView]
  );

  const onBeforeImport = useCallback(
    (files: File[]) => {
      if (IMPORT_TYPES.taskBudget === importType) {
        setSelectedFiles(files);
        setMapTabVisible(true);
        setActiveTab("map");
      }
    },
    [importType]
  );

  // Handle asyncTaskImportResult if provided
  React.useEffect(() => {
    if (asyncTaskImportResult) {
      onAfterRecordsImport(asyncTaskImportResult, importType);
    }
  }, [asyncTaskImportResult, importType, onAfterRecordsImport]);

  const getTitle = () => {
    const tabTitles: Record<string, string> = {
      upload: "Import",
      map: "Map",
      download: "Export",
      feedback: "Import Feedback",
      history: "History"
    };
    const action = tabTitles[activeTab] || "Import / Export";
    return `${action} ${importHeader} From CSV file`;
  };

  return (
    <BaseModal
      record={record}
      onClose={onClose}
      title={getTitle()}
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "min(93em, 100vw)",
        height: "min(800px, 100vh)",
        minWidth: "30em",
        maxWidth: "100vw",
        minHeight: "600px",
        maxHeight: "100vh"
      }}
      modal={true}
      className="resize"
    >
      <BaseModalBody className="!p-0 flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-background p-1 h-auto">
            <TabsTrigger value="upload" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Import
            </TabsTrigger>
            {mapTabVisible && (
              <TabsTrigger value="map" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
                Map
              </TabsTrigger>
            )}
            <TabsTrigger value="download" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Export
            </TabsTrigger>
            {importFeedbackTabVisible && (
              <TabsTrigger value="feedback" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
                Import Feedback
              </TabsTrigger>
            )}
            <TabsTrigger value="history" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="flex-1 data-[state=inactive]:hidden" forceMount>
            <UploadTab
              ref={uploadTabRef}
              record={record}
              context={context}
              canEdit={canEdit}
              onBeforeImport={onBeforeImport}
              onAfterImport={(data) => onAfterRecordsImport(data, importType)}
            />
          </TabsContent>

          {mapTabVisible && (
            <TabsContent value="map" className="flex-1 data-[state=inactive]:hidden">
              <MapTab
                record={record}
                context={context}
                files={selectedFiles}
                onFinalizeImport={(file) => uploadTabRef.current?.importEntity(file)}
              />
            </TabsContent>
          )}

          <TabsContent value="download" className="flex-1 data-[state=inactive]:hidden">
            <DownloadTab
              record={record}
              context={context}
              isEntityArchive={ARCHIVED_EXPORT_TYPES.includes(record.importType)}
            />
          </TabsContent>

          {importFeedbackTabVisible && (
            <TabsContent value="feedback" className="flex-1 data-[state=inactive]:hidden">
              <ImportFeedbackTab importResponseData={importResponseData} labelIndex={labelIndex} />
            </TabsContent>
          )}

          <TabsContent value="history" className="flex-1 data-[state=inactive]:hidden">
            <HistoryTab record={record} context={context} />
          </TabsContent>
        </Tabs>
      </BaseModalBody>
    </BaseModal>
  );
};